import { getApps, getApp, initializeApp } from 'firebase/app';
import { getAnalytics, isSupported as analyticsIsSupported, type Analytics } from 'firebase/analytics';
import {
  initializeFirestore,
  getFirestore,
  connectFirestoreEmulator,
  persistentLocalCache as _persistentLocalCache,
  persistentMultipleTabManager as _persistentMultipleTabManager,
  memoryLocalCache,
  type Firestore
} from 'firebase/firestore';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

// Export Firestore functions for use in domain modules
export {
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  addDoc,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  getDocs,
  getCountFromServer,
  DocumentSnapshot,
  QueryDocumentSnapshot,
  type DocumentData,
  QueryConstraint,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
// Export Unsubscribe type for use in hooks
export type { Unsubscribe } from 'firebase/firestore';
// Export Firebase Auth functions for use in domain modules
export {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth';

const cfg = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = getApps().length ? getApp() : initializeApp(cfg);

const useEmulators = import.meta.env.VITE_USE_EMULATORS === '1';

// ═══════════════════════════════════════════════════════════════════════════════
// SECURITY: Prevent emulator usage in staging/production environments
// This is a critical safety check to ensure emulators are NEVER used in deployed builds.
// DO NOT REMOVE OR MODIFY THIS CHECK without explicit approval.
// ═══════════════════════════════════════════════════════════════════════════════
const mode = import.meta.env.MODE;
const isDeployedEnvironment = mode === 'staging' || mode === 'production';
if (useEmulators && isDeployedEnvironment) {
  // This error will be visible in browser console and break the app intentionally
  // to prevent any data from being sent to local emulators from deployed sites
  const errorMessage = `[CRITICAL SECURITY ERROR] Emulators are enabled in ${mode} mode. ` +
    `This is a misconfiguration that would cause the app to call localhost:9099 from a deployed site. ` +
    `Check VITE_USE_EMULATORS environment variable and rebuild with VITE_USE_EMULATORS=false.`;
  console.error(errorMessage);
  throw new Error(errorMessage);
}

// PERFORMANCE: Enable offline persistence for time tracking and other features
// This allows the app to work offline and sync when connection is restored
// Firebase v12: Use initializeFirestore with persistentLocalCache instead of deprecated enableIndexedDbPersistence
// persistentMultipleTabManager allows multiple tabs to share the same cache
const _isCypress = typeof window !== 'undefined' &&
  ((window as Window & { Cypress?: unknown; IS_CYPRESS?: unknown }).Cypress || (window as Window & { IS_CYPRESS?: unknown }).IS_CYPRESS || window.location.search.includes('testing=true'));

// Firebase initialization logging removed for production security

/**
 * E2E/Emulator stability: force long-polling when using emulators.
 *
 * Why: Firestore WebChannel + emulator can intermittently throw INTERNAL ASSERTION FAILED
 * under fast navigation / listener churn (seen in Playwright).
 * Long-polling avoids that transport path.
 */
type FirestoreInitOptions = Parameters<typeof initializeFirestore>[1];

let db: Firestore;
try {
  // Try to initialize Firestore with settings (memory cache, long polling for emulators)
  db = initializeFirestore(app, ({
    localCache: memoryLocalCache(),
    ...(useEmulators ? { experimentalForceLongPolling: true } : {}),
  } as unknown as FirestoreInitOptions));
} catch (e: unknown) {
  // If Firestore is already initialized (e.g. HMR or race condition), use the existing instance
  const err = e as { code?: string; message?: string };
  if (err.code === 'failed-precondition' || err.message?.includes('already initialized')) {
    db = getFirestore(app);
  } else {
    throw e;
  }
}

const auth = getAuth(app);
const region = import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION || 'europe-west3';
const fn = getFunctions(app, region);
const st = getStorage(app);

if (useEmulators) {
  const host = import.meta.env.VITE_EMU_HOST || '127.0.0.1';
  const functionsPort = Number(import.meta.env.VITE_FUNCTIONS_EMULATOR_PORT || 5001);

  console.log('[Firebase] Connecting to emulators:');
  console.log(`  - Firestore: ${host}:${Number(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT || 8080)}`);
  console.log(`  - Auth: ${host}:${Number(import.meta.env.VITE_AUTH_EMULATOR_PORT || 9099)}`);
  console.log(`  - Functions: ${host}:${functionsPort}`);
  console.log(`  - Storage: ${host}:${Number(import.meta.env.VITE_STORAGE_EMULATOR_PORT || 9199)}`);

  connectFirestoreEmulator(db, host, Number(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT || 8080));
  connectAuthEmulator(
    auth,
    `http://${host}:${Number(import.meta.env.VITE_AUTH_EMULATOR_PORT || 9099)}`,
    { disableWarnings: true }
  );
  connectFunctionsEmulator(fn, host, functionsPort);
  connectStorageEmulator(st, host, Number(import.meta.env.VITE_STORAGE_EMULATOR_PORT || 9199));

  console.log('[Firebase] Emulator connections established successfully');
}

// Firebase Analytics — lazy init (not supported in emulator or non-browser envs)
let analyticsInstance: Analytics | null = null;
if (!useEmulators) {
  analyticsIsSupported().then((supported) => {
    if (supported) {
      analyticsInstance = getAnalytics(app);
    }
  });
}

export function getFirebaseAnalytics(): Analytics | null {
  return analyticsInstance;
}

// Backward-compatible exports for existing imports
export const firebaseApp = app;
export const functions = fn;
export const storage = st;
export const firestore = db; // Alias for db
export { app, db, auth, fn, st };
